import { CircularProgress, FormControl, Icon, IconButton, InputLabel, MenuItem, Select, TableCell, TableRow, TextField, Grid } from "@material-ui/core";
import { useState, useEffect, Fragment } from 'react';
import ArtApi from "../../api/ArtApi";
import BaseEventTypes from '../../models/BaseEventTypes';
import ConfidenceLevels from "../../models/ConfidenceLevels";
import IReviewedEvent from "../../models/ReviewedEvent";
import SentreeEventTypes from '../../models/Sentree/SentreeEventTypes';
import { hasSubType, AnyEventTypes, getSubType, AnySubEventTypes } from "../../models/EventTypeUtils";
import WaterEventTypes from "../../models/Water/WaterEventTypes";
import { useAuthDataContext } from "../AuthDataProvider";
import UserInfo from "../UserInfo";

interface ILabelledEventRowProps {
    taggedEvent: IReviewedEvent | null;
    eventId: string;
    editable: boolean;
    dataScience: boolean;
    eventTypes: string;
    steadyStateMode: boolean;
}

export const LabelledEventRow = (props: ILabelledEventRowProps) => {
    const eventTypes = props.eventTypes === "Water" ? WaterEventTypes : SentreeEventTypes;
    const { user } = useAuthDataContext();

    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [isSteadyState, setIsSteadyState] = useState(props?.steadyStateMode);

    const [start, setStart] = useState(0);
    const [end, setEnd] = useState<number>(0);
    const [height, setHeight] = useState<number>(0);
    const [eventType, setEventType] = useState<AnyEventTypes>(BaseEventTypes.unsure);
    const [eventSubType, setEventSubType] = useState<AnySubEventTypes | null>(null);
    const [eventStage, setEventStage] = useState<number | null>(null);
    const [comment, setComment] = useState<string>("");
    const [recordId, setRecordId] = useState<string | null>(null);
    const [userId, setUserId] = useState<string>(user ? user.user_details.user_id : "unknown");
    const [savedEvent, setSavedEvent] = useState<IReviewedEvent | null>(props?.taggedEvent);
    const [display, setDisplay] = useState<boolean>(true);
    const [inhibitedBySteadyState, setInhibitedBySteadyState] = useState<boolean>(true);
    const [confidenceLevel, setConfidenceLevel] = useState<ConfidenceLevels>(ConfidenceLevels.Unset);

    const [startFocus, setStartFocus] = useState<boolean>(false)
    const [endFocus, setEndFocus] = useState<boolean>(false)

    const saveLabelledEvent = () => {
        setIsLoading(true);
        ArtApi.updateTaggedEvent(props.eventId, recordId, start, end, comment, eventType, eventSubType, eventStage, height, userId, props.dataScience, confidenceLevel)
            .then(response => {
                setIsEdit(false);
                setIsLoading(false);
                setError(null);
                setRecordId(response.response);
                setSavedEvent({ start, end, height, comment, eventType, eventSubType, eventStage, user: userId, id: props.eventId, confidence: confidenceLevel })
            })
            .catch(error => {
                setError(error.message);
                setIsLoading(false);
            });
    }

    const cancelEditLabelledEvent = () => {
        setIsEdit(false);
        if (savedEvent !== null) {
            setStart(savedEvent.start);
            setEnd(savedEvent.end ? savedEvent.end : 0);
            setHeight(savedEvent.height ? savedEvent.height : 0);
            setEventType(savedEvent.eventType);
            setEventSubType(savedEvent.eventSubType);
            setEventStage(savedEvent.eventStage);
            setComment(savedEvent.comment ? savedEvent.comment : "");
            setRecordId(savedEvent.id);
            setUserId(savedEvent.user)
            setConfidenceLevel(savedEvent.confidence)
        } else if (props.taggedEvent !== null) {
            setStart(props.taggedEvent.start);
            setEnd(props.taggedEvent.end ? props.taggedEvent.end : 0);
            setHeight(props.taggedEvent.height ? props.taggedEvent.height : 0);
            setEventType(props.taggedEvent.eventType);
            setEventSubType(props.taggedEvent.eventSubType);
            setEventStage(props.taggedEvent.eventStage);
            setComment(props.taggedEvent.comment ? props.taggedEvent.comment : "");
            setRecordId(props.taggedEvent.id);
            setUserId(props.taggedEvent.user);
            setConfidenceLevel(props.taggedEvent.confidence);
        } else {
            setDisplay(false);
        }
    }

    const deleteLabelledEvent = function () {
        // TODO
        setIsLoading(true);
        ArtApi.deleteTaggedEvent(props.eventId, recordId).then(response => {
            setDisplay(false);
        }).catch(error => {
            setError(error.message);
        }).finally(() => {
            setIsLoading(false);
        });
    }

    useEffect(() => {
        if (props.taggedEvent !== null) {
            setStart(props.taggedEvent.start);
            setEnd(props.taggedEvent.end ? props.taggedEvent.end : 0);
            setHeight(props.taggedEvent.height ? props.taggedEvent.height : 0);
            setEventType(props.taggedEvent.eventType);
            setEventSubType(props.taggedEvent.eventSubType);
            setEventStage(props.taggedEvent.eventStage);
            setComment(props.taggedEvent.comment ? props.taggedEvent.comment : "");
            setRecordId(props.taggedEvent.id);
            setUserId(props.taggedEvent.user);
            setConfidenceLevel(props.taggedEvent.confidence);
        } else {
            if (isSteadyState) {
                setEventType(SentreeEventTypes.steadyState);
                setInhibitedBySteadyState(false);
            }
            setIsEdit(true);
        }
    }, [props.taggedEvent]);

    useEffect(() => setIsSteadyState(props.steadyStateMode), [props.steadyStateMode]);

    useEffect(() => setInhibitedBySteadyState(
        isSteadyState && (eventType !== SentreeEventTypes.steadyState)
    ), [isSteadyState, eventType]);

    if (!display) {
        return null;
    }

    if (inhibitedBySteadyState) {
        return (
            <TableRow>
                <TableCell colSpan={10}>
                    <Grid container alignItems="center" spacing={2}>
                        <Grid item>
                            <Icon>warning</Icon>
                        </Grid>
                        <Grid item>
                            Non-steady state event.
                            Exit steady state mode to remove it before completing the review.
                        </Grid>
                    </Grid>
                </TableCell>
            </TableRow>
        )
    }

    return (
        <TableRow>
            <TableCell>{isEdit ?
                <TextField id={startFocus ? "focus": "start"}
                    label="Hour"
                    onChange={(e) => setStart(parseFloat(e.target.value))}
                    margin="normal"
                    type="number"
                    value={start}
                    disabled={isLoading}
                    onFocus={(e) => {
                        setEndFocus(false)
                        setStartFocus(true)
                      }}
                /> :
                start}
            </TableCell>
            <TableCell>{isEdit ?
                <TextField id={endFocus ? "focus": "end"}
                    label="Hour"
                    onChange={(e) => setEnd(parseFloat(e.target.value))}
                    margin="normal"
                    type="number"
                    value={end}
                    disabled={isLoading}
                    onFocus={(e) => {
                        setStartFocus(false)
                        setEndFocus(true)
                      }}
                /> :
                end === 0 ? null : end}
            </TableCell>
            <TableCell>{isEdit ?
                <TextField id="start"
                    label="Height"
                    onChange={(e) => setHeight(parseFloat(e.target.value))}
                    margin="normal"
                    type="number"
                    value={height}
                    disabled={isLoading || eventType === BaseEventTypes.normal || eventType === BaseEventTypes.unsure}
                /> :
                height === 0 ? null : height}
            </TableCell>
            {!isSteadyState ? (<TableCell valign="bottom">
                {isEdit ?
                    <FormControl style={{ minWidth: 120 }}>
                        <InputLabel shrink id="EventTypeLabel">
                            Event Type
                        </InputLabel>
                        <Select
                            value={eventType}
                            labelId="EventTypeLabel"
                            onChange={(e) => {
                                setEventType(e.target.value as AnyEventTypes);
                                if (!hasSubType(eventType)) {
                                    setEventSubType(null);
                                }
                            }}
                            inputProps={{
                                name: "eventType",
                                id: "eventType"
                            }}
                            disabled={isLoading}
                            fullWidth={true}
                            labelWidth={100}
                        >
                            {Object.values({...eventTypes, ...BaseEventTypes}).map((key, i) =>
                                <MenuItem key={i} value={key}>{key}</MenuItem>
                            )}

                        </Select></FormControl> : eventType}</TableCell>
            ) : null}
            <TableCell valign="bottom">
                {isEdit ?
                    <FormControl style={{ minWidth: 120 }}>
                        <InputLabel shrink id="EventSubTypeLabel">
                            Event Subtype
                        </InputLabel>
                        <Select
                            value={eventSubType}
                            labelId="EventSubTypeLabel"
                            onChange={(e) => { setEventSubType(e.target.value as AnySubEventTypes) }}
                            inputProps={{
                                name: "eventSubType",
                                id: "eventSubType"
                            }}
                            disabled={isLoading || !hasSubType(eventType)}
                            fullWidth={true}
                            labelWidth={100}
                        >
                            {
                            Object.values({...getSubType(eventType)}).map((key, i) =>
                                <MenuItem key={i} value={key}>{key}</MenuItem>
                            )}

                        </Select>
                    </FormControl> : eventSubType}
            </TableCell>
            {isSteadyState ? (<TableCell>{isEdit ?
                <TextField id="eventStage"
                    label="Stage"
                    onChange={e => setEventStage(parseInt(e.target.value))}
                    type="number"
                    margin="normal"
                    value={eventStage}
                    disabled={isLoading || eventType !== SentreeEventTypes.steadyState}
                /> : eventStage}
            </TableCell>
            ) : null}
            <TableCell>{isEdit ?
                <TextField id="start"
                    label="Comment"
                    onChange={(e) => setComment(e.target.value)}
                    margin="normal"
                    value={comment}
                    disabled={isLoading}
                /> :
                comment}
            </TableCell>
            <TableCell>
                {isEdit ?
                    <FormControl style={{ minWidth: 120 }}>
                        <InputLabel shrink id="ConfidenceLevelLabel">
                            Confidence Level
                      </InputLabel>
                        <Select
                            value={confidenceLevel}
                            labelId="ConfidenceLevelLabel"
                            onChange={(e) => { setConfidenceLevel(e.target.value as ConfidenceLevels) }}
                            inputProps={{
                                name: "confidenceLevel",
                                id: "confidenceLevel"
                            }}
                            disabled={isLoading}
                            fullWidth={true}
                            labelWidth={100}
                        >
                            {Object.values([ConfidenceLevels.Definite, ConfidenceLevels.Probable, ConfidenceLevels.Unsure, ConfidenceLevels.Unset]).map((key,  i) =>
                                <MenuItem key={i} value={key}>{key}</MenuItem>
                            )}

                        </Select></FormControl> : confidenceLevel}

            </TableCell>

            <TableCell>{props.taggedEvent ? <UserInfo userId={props.taggedEvent.user} /> : "You"}</TableCell>
            <TableCell>
                {(userId === user?.user_details.user_id && props.editable) ?
                    isLoading ?
                        <CircularProgress size={24} /> :
                        isEdit ?
                            <Fragment>
                                <IconButton
                                    key="save"
                                    aria-label="save"
                                    color="inherit"
                                    onClick={() => saveLabelledEvent()}
                                >
                                    <Icon>save</Icon>
                                </IconButton>
                                <IconButton
                                    key="cancel"
                                    aria-label="cancel"
                                    color="inherit"
                                    onClick={() => cancelEditLabelledEvent()}
                                >
                                    <Icon>cancel</Icon>
                                </IconButton>
                            </Fragment> :
                            <IconButton
                                key="close"
                                aria-label="close"
                                color="inherit"
                                onClick={() => setIsEdit(true)}
                            >
                                <Icon>edit</Icon>
                            </IconButton>
                    : null
                }
                {error}
            </TableCell>
            <TableCell>
                {(userId === user?.user_details.user_id && props.editable) ?
                    <IconButton
                        key="delete"
                        aria-label="delete"
                        color="inherit"
                        onClick={deleteLabelledEvent}
                    >
                        <Icon>delete</Icon>
                    </IconButton>
                : null}
            </TableCell>
        </TableRow>
    )
}